import { useId, type ReactNode } from "react";
import { cx } from "./cx";

export interface FormFieldProps {
  label: ReactNode;
  /** Render `(control) => ...` so the field wires id + aria-describedby + aria-invalid for you. */
  children: (controlProps: {
    id: string;
    "aria-describedby"?: string;
    "aria-invalid"?: boolean;
    "aria-required"?: boolean;
  }) => ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
}

/**
 * FormField — the accessible label+control+hint+error wrapper. The control receives a generated id,
 * aria-describedby (hint/error), aria-invalid, and aria-required, so every field is screen-reader
 * complete with zero per-field boilerplate.
 */
export function FormField({ label, children, hint, error, required, className }: FormFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = cx(hint ? hintId : "", error ? errId : "").trim() || undefined;

  return (
    <div className={cx("nv-formfield", className)}>
      <label className="nv-formfield__label" htmlFor={id}>
        {label}
        {required && (
          <span className="nv-formfield__req" aria-hidden>
            *
          </span>
        )}
      </label>
      {children({
        id,
        ...(describedBy ? { "aria-describedby": describedBy } : {}),
        ...(error ? { "aria-invalid": true } : {}),
        ...(required ? { "aria-required": true } : {}),
      })}
      {hint && !error && (
        <span className="nv-formfield__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="nv-formfield__error" id={errId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
